import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout';
import { Badge, Card, EmptyState, ErrorNote, Input, Loading, Select, Table, Td, Th } from '../components/ui';
import { type Company, type LifecycleStage } from '../lib/api';
import { humanize, relative } from '../lib/format';
import { useApi, useDebounced } from '../lib/hooks';

const LIFECYCLE_STAGES: LifecycleStage[] = [
  'lead',
  'engaged',
  'qualified',
  'customer',
  'churned',
  'disqualified',
];

const LIFECYCLE_TONE: Record<LifecycleStage, 'neutral' | 'blue' | 'purple' | 'green' | 'amber'> = {
  lead: 'neutral',
  engaged: 'blue',
  qualified: 'purple',
  customer: 'green',
  churned: 'amber',
  disqualified: 'amber',
};

export function Companies() {
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const debouncedSearch = useDebounced(search);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '100' });
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (stage) params.set('lifecycle_stage', stage);
    return `/api/companies?${params.toString()}`;
  }, [debouncedSearch, stage]);

  const companies = useApi<{ companies: Company[]; total: number }>(path);
  const rows = companies.data?.companies ?? [];

  return (
    <>
      <PageHeader
        title="Companies"
        description="The account record — every organization behind your prospects, with its own lifecycle stage and combined history."
      />

      <Card className="mb-4">
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Search by name…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-64"
          />
          <Select value={stage} onChange={(event) => setStage(event.target.value)}>
            <option value="">All stages</option>
            {LIFECYCLE_STAGES.map((s) => (
              <option key={s} value={s}>
                {humanize(s)}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      <Card bodyClassName="p-0">
        {companies.loading ? (
          <Loading />
        ) : companies.error ? (
          <ErrorNote message={companies.error} onRetry={companies.reload} />
        ) : rows.length === 0 ? (
          <EmptyState title="No companies yet" description="Companies are created automatically as prospects come in." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Domain</Th>
                <Th>Stage</Th>
                <Th>People</Th>
                <Th>Last touch</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((company) => (
                <tr key={company.id} className="hover:bg-ink-50">
                  <Td>
                    <Link to={`/companies/${company.id}`} className="font-medium text-accent-700 hover:underline">
                      {company.name}
                    </Link>
                  </Td>
                  <Td>{company.domain ?? '—'}</Td>
                  <Td>
                    <Badge tone={LIFECYCLE_TONE[company.lifecycle_stage]}>{humanize(company.lifecycle_stage)}</Badge>
                  </Td>
                  <Td>{company.prospect_count ?? 0}</Td>
                  <Td>{company.last_touch_at ? relative(company.last_touch_at) : '—'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
